import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Autocomplete,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCircleDot,
  IconArrowLeft,
  IconClockHour4,
  IconMail,
  IconPencil,
  IconPlus,
  IconFilePlus,
  IconPrinter,
  IconReceipt,
  IconSend,
  IconSignature,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { odpriPdf } from '../api/pdf';
import { casIzMs, datum, datumZaApi, denar, kolicina, odstotek, stevilo } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import {
  GLAVNI_PREHOD,
  obrezano,
  oznakaPrehoda,
  opisVozila,
  STATUSI,
  VRSTE_POSTAVK,
  type StatusNaloga,
  type VrstaPostavke,
} from './delavnica';
import { DodajPostavko, type ZacetekPostavke } from './DodajPostavko';
import { IzbiraCenika } from './IzbiraCenika';
import { StrankaNaloga } from './StrankaNaloga';
import { ustvariStranko, vpisanaNova, type NovaStranka } from './stranka';
import { UreNaloga } from './UreNaloga';
import { ObvestiloStranke, type NacinObvestila } from './ObvestiloStranke';
import { PodpisStranke } from './PodpisStranke';
import { SlikeNaloga } from './SlikeNaloga';
import { usePravica } from '../seja/pravice';

type Nalog = Shema['DelovniNalogPodrobnosti'];
type Postavka = Shema['PostavkaNaloga'];
type Oseba = Shema['OsebaPodatki'];
/** Postavka v urejanju: številska polja so lahko prazna, dokler jih uporabnik ne vpiše. */
type VrsticaPostavke = Omit<Postavka, 'kolicina' | 'cena' | 'popust' | 'ddvStopnja'> & {
  kolicina: number | string;
  cena: number | string;
  popust: number | string;
  ddvStopnja: number | string;
};

const stevilka = (v: number | string) => (typeof v === 'number' ? v : undefined);

/**
 * Delovni nalog (modul Delavnica): status z gumbi za naslednje korake (začni delo, čaka dele, zaključi, prevzem),
 * vozilo in stranka s kontaktom, podatki sprejema, opravljene ure mehanikov ({@link UreNaloga}), postavke (»Dodaj
 * postavko« iz cenika brez urejanja naloga, »Razporedi ure« iz nerazporejenih ur, »Uredi« → urejanje vsega; zneske
 * izračuna strežnik), zgodovina sprememb. Po prevzemu se nalog ne ureja več.
 */
export function PodrobnostiNaloga() {
  const id = Number(useParams().id);
  const poizvedba = useQuery({
    queryKey: ['delovni-nalog', id],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/nalogi/{id}', { signal, params: { path: { id } } })),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Delovnega naloga ni bilo mogoče naložiti"
      />
    );
  }
  // ob novih podatkih (po shranjevanju ali spremembi statusa) se obrazec ponastavi
  return <Nalog key={poizvedba.dataUpdatedAt} nalog={poizvedba.data} />;
}

function vObrazec(n: Nalog) {
  return {
    kupec: n.kupecId ? ({ id: n.kupecId, ime: n.kupec ?? '' } as Oseba) : null,
    nova: null as NovaStranka,
    km: n.km ?? ('' as number | string),
    tezava: n.tezava ?? '',
    opomba: n.opomba ?? '',
    mehanik: n.mehanikId ? String(n.mehanikId) : null,
    predviden: n.predvidenZakljucek ?? null,
    postavke: (n.postavke ?? []).map((p): VrsticaPostavke => ({
      ...p,
      kolicina: p.kolicina ?? '',
      cena: p.cena ?? '',
      popust: p.popust ?? 0,
      ddvStopnja: p.ddvStopnja ?? 0,
    })),
  };
}

function Nalog({ nalog }: { nalog: Nalog }) {
  const odjemalec = useQueryClient();
  const [urejanje, setUrejanje] = useState(false);
  const [obrazec, setObrazec] = useState(() => vObrazec(nalog));
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [nova, setNova] = useState<ZacetekPostavke | null>(null);
  const [obvestilo, setObvestilo] = useState<NacinObvestila | null>(null);
  const [podpis, setPodpis] = useState(false);
  const odobritev = useMutation({
    mutationFn: () =>
      podatki(api.POST('/api/delavnica/nalogi/{id}/odobritev', { params: { path: { id: nalog.id! } } })),
    onMutate: () => setNapaka(null),
    onSuccess: (n) => {
      notifications.show({ color: 'green', message: `Povezava za odobritev je poslana na ${n.email}.` });
      void posodobljen(n);
    },
    onError: (e) => setNapaka(sporocilo(e)),
  });
  const pdf = useMutation({
    mutationFn: () =>
      odpriPdf(api.GET('/api/delavnica/nalogi/{id}/pdf', { params: { path: { id: nalog.id! } }, parseAs: 'blob' })),
    onError: (e) => setNapaka(sporocilo(e)),
  });
  const status = nalog.status as StatusNaloga;

  const moznosti = useQuery({
    queryKey: ['delavnica-moznosti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/nalogi/moznosti', { signal })),
    enabled: !!nalog.urejanje,
  });
  // porabljene ure, ki še niso na postavkah dela (z enoto h)
  const nerazporejene = Math.max(0, Number(nalog.porabljeneUre ?? 0) - Number(nalog.zaracunaneUre ?? 0));

  const spremenjeno = urejanje && JSON.stringify(obrazec) !== JSON.stringify(vObrazec(nalog));
  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const opozori = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', opozori);
    return () => window.removeEventListener('beforeunload', opozori);
  }, [spremenjeno]);

  const posodobljen = async (n: Nalog) => {
    odjemalec.setQueryData(['delovni-nalog', nalog.id], n);
    await odjemalec.invalidateQueries({ queryKey: ['delovni-nalogi'] });
    await odjemalec.invalidateQueries({ queryKey: ['delavnica-pregled'] });
    await odjemalec.invalidateQueries({ queryKey: ['vozilo-stranke'] });
  };

  const shrani = useMutation({
    mutationFn: async () => {
      // vpisana nova stranka se ustvari ob shranjevanju (tudi brez klika »Dodaj«)
      let kupec = obrazec.kupec;
      if (!kupec && obrazec.nova && vpisanaNova(obrazec.nova)) {
        try {
          kupec = await ustvariStranko(obrazec.nova);
        } catch (e) {
          throw e instanceof NapakaApi && e.polja.ime
            ? new NapakaApi(e.polja.ime, e.koda, e.status, { 'stranka.ime': e.polja.ime })
            : e;
        }
        setObrazec((x) => ({ ...x, kupec, nova: null }));
      }
      return podatki(
        api.PUT('/api/delavnica/nalogi/{id}', {
          params: { path: { id: nalog.id! } },
          body: {
            kupecId: kupec?.id ?? undefined,
            km: stevilka(obrazec.km),
            tezava: obrazec.tezava.trim(),
            opomba: obrazec.opomba.trim(),
            mehanikId: obrazec.mehanik ? Number(obrazec.mehanik) : undefined,
            predvidenZakljucek: obrazec.predviden ?? undefined,
            postavke: obrazec.postavke.map((p) => ({
              ...p,
              kolicina: stevilka(p.kolicina),
              cena: stevilka(p.cena),
              popust: stevilka(p.popust),
              ddvStopnja: stevilka(p.ddvStopnja),
              naziv: obrezano(p.naziv),
              enota: obrezano(p.enota),
            })),
          },
        }),
      );
    },
    onMutate: () => {
      setPolja({});
      setNapaka(null);
    },
    onSuccess: async (n) => {
      notifications.show({ color: 'green', message: 'Nalog je shranjen.' });
      await posodobljen(n);
    },
    onError: (e) => {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Preverite označena polja.');
      } else {
        setNapaka(sporocilo(e));
      }
    },
  });

  const prehod = useMutation({
    mutationFn: (na: StatusNaloga) =>
      podatki(
        api.POST('/api/delavnica/nalogi/{id}/status', { params: { path: { id: nalog.id! } }, body: { status: na } }),
      ),
    onMutate: () => setNapaka(null),
    // brez obvestila: značka statusa se vidno spremeni, obvestilo pa bi prekrilo gumb za naslednji korak
    onSuccess: posodobljen,
    onError: (e) => setNapaka(sporocilo(e)),
  });

  const glavni = GLAVNI_PREHOD[status];
  const dejanja = urejanje ? (
    <Group gap="xs">
      <Button
        variant="subtle"
        onClick={() => {
          setObrazec(vObrazec(nalog));
          setPolja({});
          setNapaka(null);
          setUrejanje(false);
        }}
        disabled={shrani.isPending}
      >
        Prekliči
      </Button>
      <Button onClick={() => shrani.mutate()} loading={shrani.isPending}>
        Shrani
      </Button>
    </Group>
  ) : (
    <Group gap="xs">
      {(nalog.naslednjiStatusi ?? []).map((na) => (
        <Button
          key={na}
          variant={na === glavni ? 'filled' : 'default'}
          onClick={() => (na === 'ZAKLJUCEN' ? setObvestilo('zakljucek') : prehod.mutate(na))}
          loading={prehod.isPending && prehod.variables === na}
          disabled={prehod.isPending && prehod.variables !== na}
        >
          {oznakaPrehoda(status, na)}
        </Button>
      ))}
      {status === 'ZAKLJUCEN' && !nalog.obvescen && (
        <Button variant="default" leftSection={<IconMail size={18} />} onClick={() => setObvestilo('obvestilo')}>
          Obvesti stranko
        </Button>
      )}
      {nalog.urejanje && (
        <Button variant="default" leftSection={<IconPencil size={18} />} onClick={() => setUrejanje(true)}>
          Uredi
        </Button>
      )}
      {(status === 'ZAKLJUCEN' || status === 'PREVZET') && !nalog.racunId && (nalog.postavke ?? []).length > 0 && (
        <Button component={Link} to={`/racuni/nov?nalog=${nalog.id}`} leftSection={<IconReceipt size={18} />}>
          Izdaj račun
        </Button>
      )}
      {nalog.urejanje && !nalog.predracunId && (nalog.postavke ?? []).length > 0 && (
        <Button
          component={Link}
          to={`/predracuni/nov?nalog=${nalog.id}`}
          variant="default"
          leftSection={<IconFilePlus size={18} />}
        >
          Predračun
        </Button>
      )}
      <Button
        variant="default"
        leftSection={<IconPrinter size={18} />}
        loading={pdf.isPending}
        onClick={() => pdf.mutate()}
      >
        PDF
      </Button>
      <Button
        component={Link}
        to={`/delavnica/gume/nov?nalog=${nalog.id}`}
        variant="default"
        leftSection={<IconCircleDot size={18} />}
      >
        Shrani gume
      </Button>
    </Group>
  );

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/nalogi"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Delovni nalogi
      </Button>
      <GlavaZaslona
        naslov={`Nalog ${nalog.stevilka}`}
        opis={opisVozila(nalog.vozilo)}
        pomoc="delovni-nalogi"
        dejanja={dejanja}
      />
      <Group gap="xs" mb="md">
        <Badge color={STATUSI[status].barva} size="lg" variant="light">
          {STATUSI[status].naziv}
        </Badge>
        {nalog.racunId && (
          <Anchor component={Link} to={`/racuni/${nalog.racunId}`} size="sm" fw={600}>
            Račun {nalog.stRacuna}
          </Anchor>
        )}
        {nalog.predracunId && (
          <Anchor component={Link} to={`/predracuni/${nalog.predracunId}`} size="sm" fw={600}>
            Predračun {nalog.stPredracuna}
          </Anchor>
        )}
        <Text size="sm" c="dimmed">
          Sprejeto {casIzMs(nalog.sprejeto)}
          {nalog.zakljuceno ? ` · zaključeno ${casIzMs(nalog.zakljuceno)}` : ''}
          {nalog.prevzeto ? ` · prevzeto ${casIzMs(nalog.prevzeto)}` : ''}
        </Text>
      </Group>
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica
            naslov="Vozilo"
            dejanja={
              nalog.vozilo?.id ? (
                <Anchor component={Link} to={`/delavnica/vozila/${nalog.vozilo.id}`} size="sm">
                  Servisna knjiga
                </Anchor>
              ) : undefined
            }
          >
            <Podatki
              vrstice={[
                ['Registrska', nalog.vozilo?.registrska],
                ['Vozilo', [nalog.vozilo?.znamka, nalog.vozilo?.model].filter(Boolean).join(' ')],
                ['Letnik', nalog.vozilo?.letnik],
                ['VIN', nalog.vozilo?.vin],
                ['Motor', [nalog.vozilo?.motor, nalog.vozilo?.gorivo].filter(Boolean).join(' · ')],
              ]}
            />
          </Kartica>
          <Kartica
            naslov="Stranka"
            dejanja={
              !urejanje && nalog.urejanje ? (
                <Button
                  size="sm"
                  variant={nalog.podpisano ? 'subtle' : 'light'}
                  leftSection={<IconSignature size={16} />}
                  onClick={() => setPodpis(true)}
                >
                  {nalog.podpisano ? 'Podpisano – znova' : 'Podpis stranke'}
                </Button>
              ) : undefined
            }
          >
            {urejanje ? (
              <StrankaNaloga
                kupec={obrazec.kupec}
                izberi={(o) => setObrazec((x) => ({ ...x, kupec: o }))}
                nova={obrazec.nova}
                nastaviNovo={(n) => setObrazec((x) => ({ ...x, nova: n }))}
                polja={polja}
              />
            ) : (
              <Podatki
                vrstice={[
                  [
                    'Ime',
                    nalog.kupecId ? (
                      <Anchor component={Link} to={`/osebe/kupci/${nalog.kupecId}`}>
                        {nalog.kupec}
                      </Anchor>
                    ) : (
                      ''
                    ),
                  ],
                  ['Telefon', nalog.telefon ? <Anchor href={`tel:${nalog.telefon}`}>{nalog.telefon}</Anchor> : ''],
                  ['E-pošta', nalog.email ? <Anchor href={`mailto:${nalog.email}`}>{nalog.email}</Anchor> : ''],
                ]}
              />
            )}
          </Kartica>
        </Stack>
        <Kartica naslov="Sprejem">
          {urejanje ? (
            <Stack gap="sm">
              <NumberInput
                label="Kilometri ob sprejemu"
                value={obrazec.km}
                onChange={(v) => setObrazec((x) => ({ ...x, km: v }))}
                error={polja.km}
                allowDecimal={false}
                allowNegative={false}
                thousandSeparator="."
                decimalSeparator=","
              />
              <Textarea
                label="Težava po besedah stranke"
                value={obrazec.tezava}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setObrazec((x) => ({ ...x, tezava: v }));
                }}
                onBlur={() => setObrazec((x) => ({ ...x, tezava: x.tezava.trim() }))}
                error={polja.tezava}
                autosize
                minRows={2}
              />
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                <Select
                  label="Mehanik"
                  data={(moznosti.data?.mehaniki ?? []).map((m) => ({ value: String(m.id), label: m.ime ?? '' }))}
                  value={obrazec.mehanik}
                  onChange={(v) => setObrazec((x) => ({ ...x, mehanik: v }))}
                  error={polja.mehanikId}
                  clearable
                />
                <DatePickerInput
                  label="Predviden zaključek"
                  value={obrazec.predviden}
                  onChange={(d) => setObrazec((x) => ({ ...x, predviden: datumZaApi(d) ?? null }))}
                  valueFormat="D. M. YYYY"
                  clearable
                />
              </SimpleGrid>
              <Textarea
                label="Interna opomba / diagnoza"
                value={obrazec.opomba}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setObrazec((x) => ({ ...x, opomba: v }));
                }}
                onBlur={() => setObrazec((x) => ({ ...x, opomba: x.opomba.trim() }))}
                error={polja.opomba}
                autosize
                minRows={2}
              />
            </Stack>
          ) : (
            <Podatki
              vrstice={[
                ['Kilometri', nalog.km != null ? stevilo(nalog.km) : ''],
                [
                  'Težava',
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {nalog.tezava}
                  </Text>,
                ],
                ['Mehanik', nalog.mehanik],
                ['Predviden zaključek', datum(nalog.predvidenZakljucek)],
                [
                  'Opomba',
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {nalog.opomba}
                  </Text>,
                ],
                ['Sprejel', nalog.ustvaril],
              ]}
            />
          )}
        </Kartica>
      </SimpleGrid>
      <Stack gap="md" mt="md">
        <Kartica naslov="Fotografije">
          <SlikeNaloga nalog={nalog} posodobljen={(n) => void posodobljen(n)} />
        </Kartica>
        <Kartica naslov="Opravljene ure">
          <UreNaloga nalog={nalog} posodobljen={(n) => void posodobljen(n)} />
        </Kartica>
        <Kartica
          naslov="Postavke"
          dejanja={
            !urejanje && nalog.urejanje ? (
              <Group gap="xs">
                {nerazporejene > 0 && (
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconClockHour4 size={16} />}
                    onClick={() => setNova({ vrsta: 'DELO', kolicina: nerazporejene })}
                  >
                    Razporedi ure ({kolicina(nerazporejene)} h)
                  </Button>
                )}
                {(nalog.postavke ?? []).some((p) => p.predlagano && !p.odobritev) && (
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<IconSend size={16} />}
                    loading={odobritev.isPending}
                    onClick={() => odobritev.mutate()}
                  >
                    Pošlji v odobritev
                  </Button>
                )}
                <Button size="sm" leftSection={<IconPlus size={16} />} onClick={() => setNova({ vrsta: 'DELO' })}>
                  Dodaj postavko
                </Button>
              </Group>
            ) : undefined
          }
        >
          {urejanje ? (
            <UrejanjePostavk
              postavke={obrazec.postavke}
              nastavi={(postavke) => setObrazec((x) => ({ ...x, postavke }))}
              polja={polja}
              ddvStopnja={moznosti.data?.ddvStopnja ?? 0}
              enote={moznosti.data?.enote ?? []}
            />
          ) : (
            <TabelaPostavk nalog={nalog} />
          )}
          {Number(nalog.porabljeneUre) > 0 && (
            <Text size="sm" c="dimmed" mt="xs">
              Porabljeno {kolicina(nalog.porabljeneUre)} h · zaračunano {kolicina(nalog.zaracunaneUre)} h
            </Text>
          )}
        </Kartica>
        <Kartica naslov="Zgodovina">
          <Stack gap={6}>
            {(nalog.dogodki ?? []).map((d, i) => (
              <Text key={i} size="sm">
                <Text span c="dimmed">
                  {casIzMs(d.cas)} · {d.uporabnik}
                </Text>{' '}
                {d.vrsta === 'STATUS' && d.status ? `Status: ${STATUSI[d.status as StatusNaloga].naziv}` : ''}
                {d.vrsta === 'STATUS' && d.opis ? ` – ${d.opis}` : d.vrsta !== 'STATUS' ? d.opis : ''}
              </Text>
            ))}
          </Stack>
        </Kartica>
      </Stack>
      <ObvestiloStranke
        nalog={nalog}
        nacin={obvestilo}
        zapri={() => setObvestilo(null)}
        posodobljen={(n) => void posodobljen(n)}
      />
      <PodpisStranke
        nalog={nalog}
        odprto={podpis}
        zapri={() => setPodpis(false)}
        posodobljen={(n) => void posodobljen(n)}
      />
      <DodajPostavko
        nalogId={nalog.id!}
        zacetek={nova}
        zapri={() => setNova(null)}
        ddvStopnja={moznosti.data?.ddvStopnja ?? 0}
        enote={moznosti.data?.enote ?? []}
        dodana={(n) => void posodobljen(n)}
      />
    </ZaslonSeznama>
  );
}

/** Oznaka predlagane postavke: čaka odgovor stranke, odobreno ali zavrnjeno. */
function OznakaPredloga({ odobritev }: { odobritev?: string }) {
  const [barva, besedilo] =
    odobritev === 'ODOBRENO'
      ? ['green', 'Odobreno']
      : odobritev === 'ZAVRNJENO'
        ? ['red', 'Zavrnjeno']
        : ['yellow', 'Predlagano – čaka stranko'];
  return (
    <Badge color={barva} variant="light" size="sm" ml="xs">
      {besedilo}
    </Badge>
  );
}

/** Postavke za ogled z zneski in skupnim zneskom (DDV samo, če ga je kaj). */
function TabelaPostavk({ nalog }: { nalog: Nalog }) {
  const postavke = nalog.postavke ?? [];
  if (postavke.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Ni postavk. Dodate jih z »Dodaj postavko«.
      </Text>
    );
  }
  const vrsta = (v?: string) => VRSTE_POSTAVK.find((x) => x.value === v)?.label ?? '';
  return (
    <Stack gap="sm">
      <Table.ScrollContainer minWidth={640}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vrsta</Table.Th>
              <Table.Th>Naziv</Table.Th>
              <Table.Th ta="right">Količina</Table.Th>
              <Table.Th ta="right">Cena</Table.Th>
              <Table.Th ta="right">Popust</Table.Th>
              <Table.Th ta="right">DDV</Table.Th>
              <Table.Th ta="right">Znesek</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {postavke.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>{vrsta(p.vrsta)}</Table.Td>
                <Table.Td>
                  {p.naziv}
                  {p.predlagano && <OznakaPredloga odobritev={p.odobritev} />}
                  {(p.sifra || p.dobavitelj) && (
                    <Text size="xs" c="dimmed">
                      {[p.sifra, p.dobavitelj].filter(Boolean).join(' · ')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td ta="right" className="brez-preloma">
                  {kolicina(p.kolicina)} {p.enota}
                </Table.Td>
                <Table.Td ta="right" className="brez-preloma">
                  {denar(p.cena)}
                </Table.Td>
                <Table.Td ta="right" className="brez-preloma">
                  {p.popust ? `${odstotek(p.popust)} %` : ''}
                </Table.Td>
                <Table.Td ta="right" className="brez-preloma">
                  {odstotek(p.ddvStopnja ?? 0)} %
                </Table.Td>
                <Table.Td
                  ta="right"
                  className="brez-preloma"
                  c={p.predlagano && p.odobritev !== 'ODOBRENO' ? 'dimmed' : undefined}
                  td={p.odobritev === 'ZAVRNJENO' ? 'line-through' : undefined}
                >
                  {denar(p.znesek)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Stack gap={2} align="flex-end">
        {Number(nalog.ddv) > 0 && (
          <>
            <Text size="sm">Osnova: {denar(nalog.osnova)} €</Text>
            <Text size="sm">DDV: {denar(nalog.ddv)} €</Text>
          </>
        )}
        <Text fw={700}>Skupaj: {denar(nalog.skupaj)} €</Text>
      </Stack>
    </Stack>
  );
}

/**
 * Urejanje postavk: vrsta, naziv, količina z enoto, cena enote brez DDV, popust in DDV; za dele še šifra, dobavitelj
 * in nabavna cena. Napake strežnika so {@code postavke.<i>.<polje>}.
 */
function UrejanjePostavk({
  postavke,
  nastavi,
  polja,
  ddvStopnja,
  enote,
}: {
  postavke: VrsticaPostavke[];
  nastavi: (p: VrsticaPostavke[]) => void;
  polja: Record<string, string>;
  ddvStopnja: number;
  enote: string[];
}) {
  // nabavne cene: brez pregleda skrite, brez urejanja samo za ogled (strežnik jih ohrani)
  const nabavne = usePravica('NABAVNE_CENE');
  const urejanjeNabavnih = usePravica('NABAVNE_CENE', 'UREJANJE');
  const spremeni = (i: number, sprememba: Partial<VrsticaPostavke>) =>
    nastavi(postavke.map((p, j) => (j === i ? { ...p, ...sprememba } : p)));
  const dodaj = (vrsta: VrstaPostavke) =>
    nastavi([
      ...postavke,
      { vrsta, naziv: '', kolicina: 1, enota: vrsta === 'DELO' ? 'h' : 'kos', cena: '', popust: 0, ddvStopnja },
    ]);
  const decimalno = { decimalSeparator: ',', allowNegative: false } as const;
  return (
    <Stack gap="sm">
      {postavke.map((p, i) => {
        const napaka = (k: string) => polja[`postavke.${i}.${k}`];
        return (
          <Paper key={i} withBorder radius="md" p="sm" aria-label={`Postavka ${i + 1}`}>
            <Stack gap="xs">
              <Group gap="xs" align="flex-start" wrap="wrap">
                <Select
                  label="Vrsta"
                  data={VRSTE_POSTAVK}
                  value={p.vrsta ?? null}
                  onChange={(v) => spremeni(i, { vrsta: (v as VrstaPostavke) ?? undefined })}
                  allowDeselect={false}
                  error={napaka('vrsta')}
                  w={120}
                />
                <IzbiraCenika
                  vrsta={p.vrsta}
                  naziv={p.naziv ?? ''}
                  nastaviNaziv={(naziv) => spremeni(i, { naziv, cenikId: undefined })}
                  izbrana={(c) =>
                    spremeni(i, {
                      cenikId: c.id,
                      naziv: c.naziv,
                      enota: c.enota,
                      cena: c.cena ?? '',
                      ddvStopnja: c.ddvStopnja ?? ddvStopnja,
                      sifra: c.sifra,
                      dobavitelj: c.dobavitelj,
                      nabavnaCena: c.nabavnaCena,
                    })
                  }
                  napaka={napaka('naziv')}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size={36}
                  mt={24}
                  aria-label={`Odstrani postavko ${i + 1}`}
                  onClick={() => nastavi(postavke.filter((_, j) => j !== i))}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
                <NumberInput
                  label="Količina"
                  value={p.kolicina}
                  onChange={(v) => spremeni(i, { kolicina: v })}
                  error={napaka('kolicina')}
                  decimalScale={3}
                  {...decimalno}
                />
                <Autocomplete
                  label="Enota"
                  data={enote}
                  value={p.enota ?? ''}
                  onChange={(v) => spremeni(i, { enota: v })}
                  error={napaka('enota')}
                />
                <NumberInput
                  label="Cena brez DDV"
                  value={p.cena}
                  onChange={(v) => spremeni(i, { cena: v })}
                  error={napaka('cena')}
                  decimalScale={2}
                  thousandSeparator="."
                  rightSection="€"
                  {...decimalno}
                />
                <NumberInput
                  label="Popust"
                  value={p.popust}
                  onChange={(v) => spremeni(i, { popust: v })}
                  error={napaka('popust')}
                  decimalScale={2}
                  max={100}
                  rightSection="%"
                  {...decimalno}
                />
                <NumberInput
                  label="DDV"
                  value={p.ddvStopnja}
                  onChange={(v) => spremeni(i, { ddvStopnja: v })}
                  error={napaka('ddvStopnja')}
                  decimalScale={2}
                  max={100}
                  rightSection="%"
                  {...decimalno}
                />
              </SimpleGrid>
              {p.vrsta === 'DEL' && (
                <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="xs">
                  <TextInput
                    label="Šifra dela"
                    value={p.sifra ?? ''}
                    onChange={(e) => spremeni(i, { sifra: e.currentTarget.value })}
                    error={napaka('sifra')}
                  />
                  <TextInput
                    label="Dobavitelj"
                    value={p.dobavitelj ?? ''}
                    onChange={(e) => spremeni(i, { dobavitelj: e.currentTarget.value })}
                    error={napaka('dobavitelj')}
                  />
                  {nabavne && (
                    <NumberInput
                      label="Nabavna cena"
                      disabled={!urejanjeNabavnih}
                      value={p.nabavnaCena ?? ''}
                      onChange={(v) => spremeni(i, { nabavnaCena: typeof v === 'number' ? v : undefined })}
                      error={napaka('nabavnaCena')}
                      decimalScale={2}
                      thousandSeparator="."
                      rightSection="€"
                      {...decimalno}
                    />
                  )}
                </SimpleGrid>
              )}
              <Checkbox
                label="Predlagano dodatno delo (odobri stranka)"
                checked={!!p.predlagano}
                onChange={(e) => spremeni(i, { predlagano: e.currentTarget.checked })}
                disabled={!!p.odobritev}
              />
            </Stack>
          </Paper>
        );
      })}
      <Group gap="xs">
        {VRSTE_POSTAVK.map((v) => (
          <Button key={v.value} variant="light" leftSection={<IconPlus size={16} />} onClick={() => dodaj(v.value)}>
            {v.label}
          </Button>
        ))}
      </Group>
      <Text size="xs" c="dimmed">
        Zneski se izračunajo ob shranjevanju.
      </Text>
    </Stack>
  );
}
